import { FilesetResolver, HandLandmarker } from '@mediapipe/tasks-vision';
import { drawHand } from '../lib/handUtils';
import { bus, EVENTS } from '../lib/eventBus';

const MODEL_URL =
  'https://storage.googleapis.com/mediapipe-models/hand_landmarker/hand_landmarker/float16/1/hand_landmarker.task';
const WASM_BASE = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';

const VIDEO_CONSTRAINTS = {
  audio: false,
  video: {
    facingMode: 'user',
    width: { ideal: 640 },
    height: { ideal: 480 },
    frameRate: { ideal: 30, max: 30 },
  },
};

// Phase 5 dual-hand auto-degrade thresholds.
const FPS_SAMPLER_DURATION_MS = 3000;   // measure for 3 seconds
const FPS_DEGRADE_THRESHOLD = 15;       // < 15 fps → downgrade to single

const SECOND_HAND_COLOR = {
  lineColor: '#22D3EE', // cyan
  dotEdge: '#0EA5E9',
  dotColor: '#FFFFFF',
};

/**
 * VisionAgent — wraps MediaPipe Hands + camera lifecycle (Phase 5 dual-hand).
 *
 * Pipeline:
 *   • init() creates the landmarker with numHands: 2 (dual-hand by default).
 *   • _detect() runs each frame, samples FPS for the first 3 seconds, and
 *     auto-degrades to numHands: 1 if avg FPS < 15.
 *   • One-shot decision — once set, the mode does NOT flap.
 *
 * Bus emissions:
 *   landmarks:detected → {
 *     hands: [{ landmarks: 21-point array, handedness: 'Left'|'Right', score },
 *             ... up to 2],
 *     timestamp
 *   }
 *   hands:count        → 0 | 1 | 2 (only on change)
 *   tracking:degraded  → { from: 2, to: 1, reason: 'Low FPS', measuredFps }
 *   fps:update         → number
 *   vision:state       → 'idle'|'init'|'ready'|'running'|'paused'|'error'
 *   hand:status        → { status: 'idle'|'detecting' }
 *
 * Handedness note: MediaPipe's "Left/Right" labels are from the signer's POV.
 * With selfie-mirrored video, a "Left" hand appears on the screen's RIGHT.
 * Bimanual scorers in gestureBimanual.js are symmetric so this doesn't matter.
 */
export class VisionAgent {
  constructor() {
    this.video = null;
    this.canvas = null;
    this.landmarker = null;
    this.stream = null;
    this.rafId = null;
    this.lastVideoTime = -1;
    this.fpsBuf = [];
    this.fps = 0;
    this.running = false;
    this.state = 'idle';
    this.videoMeta = { w: 0, h: 0 };
    this._lastHandStatus = null;
    this._lastHandCount = -1;

    // Phase 5 — dual-hand + sampler
    this.numHands = 2;
    this._samplerStartedAt = null;
    this._samplerDone = false;
    this._degrading = false;
    this.trackingMode = 'dual';        // 'dual' | 'single' (post-init)
    this.trackingModeReason = null;    // null | 'auto-degrade'
  }

  setState(s) {
    this.state = s;
    bus.emit(EVENTS.VISION_STATE, s);
  }

  attach(videoEl, canvasEl) {
    this.video = videoEl;
    this.canvas = canvasEl;
  }

  async _createLandmarker(numHands) {
    const fileset = await FilesetResolver.forVisionTasks(WASM_BASE);
    this.landmarker = await HandLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
      runningMode: 'VIDEO',
      numHands,
      minHandDetectionConfidence: 0.5,
      minHandPresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
    });
    // eslint-disable-next-line no-console
    console.log(`[SignVoice] HandLandmarker ready (numHands: ${numHands}, GPU)`);
  }

  async init() {
    if (this.landmarker) return;
    this.setState('init');
    this.numHands = 2;
    this._samplerStartedAt = null;
    this._samplerDone = false;
    await this._createLandmarker(this.numHands);
    // eslint-disable-next-line no-console
    console.log('[SignVoice] Dual-hand tracking enabled (numHands: 2)');
    this.trackingMode = 'dual';
    this.trackingModeReason = null;
    this.setState('ready');
  }

  async startCamera() {
    if (!this.video) throw new Error('VisionAgent: video element not attached');
    const stream = await navigator.mediaDevices.getUserMedia(VIDEO_CONSTRAINTS);
    this.stream = stream;
    this.video.srcObject = stream;
    this.video.setAttribute('playsinline', 'true');
    await this.video.play();
    const track = stream.getVideoTracks()[0];
    const settings = track.getSettings();
    this.videoMeta = {
      w: settings.width || this.video.videoWidth,
      h: settings.height || this.video.videoHeight,
    };
    // eslint-disable-next-line no-console
    console.log(
      `[SignVoice] Camera ${this.videoMeta.w}x${this.videoMeta.h} @ ${
        settings.frameRate || '?'
      }fps`,
    );
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.setState('running');
    const tick = () => {
      if (!this.running) return;
      this._detect();
      this.rafId = requestAnimationFrame(tick);
    };
    this.rafId = requestAnimationFrame(tick);
  }

  pause() {
    this.running = false;
    if (this.rafId) cancelAnimationFrame(this.rafId);
    this.rafId = null;
    if (this.canvas) {
      const ctx = this.canvas.getContext('2d');
      ctx?.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
    bus.emit(EVENTS.LANDMARKS_DETECTED, { hands: [], timestamp: performance.now() });
    if (this._lastHandCount !== 0) {
      this._lastHandCount = 0;
      bus.emit(EVENTS.HANDS_COUNT, 0);
    }
    if (this._lastHandStatus !== 'idle') {
      this._lastHandStatus = 'idle';
      bus.emit(EVENTS.HAND_STATUS, { status: 'idle' });
    }
    this.setState('paused');
  }

  async _degradeToSingleHand() {
    if (this._degrading || this.numHands === 1) return;
    this._degrading = true;
    const wasRunning = this.running;

    // Pause the loop; we'll resume after re-init.
    if (wasRunning) {
      this.running = false;
      if (this.rafId) cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    try {
      this.landmarker?.close();
    } catch {
      /* noop */
    }
    this.landmarker = null;
    this.numHands = 1;
    const measuredFps = this.fps;
    await this._createLandmarker(1);
    // eslint-disable-next-line no-console
    console.log(`[SignVoice] Dual-hand auto-degraded to single-hand. FPS=${measuredFps}`);

    this.trackingMode = 'single';
    this.trackingModeReason = 'auto-degrade';
    bus.emit(EVENTS.TRACKING_DEGRADED, {
      from: 2,
      to: 1,
      reason: 'Low FPS',
      measuredFps,
    });

    if (wasRunning) this.start();
    this._degrading = false;
  }

  _detect() {
    const v = this.video;
    const c = this.canvas;
    const lm = this.landmarker;
    if (!v || !c || !lm || v.readyState < 2) return;

    if (c.width !== v.videoWidth) c.width = v.videoWidth;
    if (c.height !== v.videoHeight) c.height = v.videoHeight;

    const now = performance.now();
    if (v.currentTime === this.lastVideoTime) return;
    this.lastVideoTime = v.currentTime;

    try {
      const res = lm.detectForVideo(v, now);
      const ctx = c.getContext('2d');
      ctx.clearRect(0, 0, c.width, c.height);

      const handsRaw = res?.landmarks || [];
      const handedness = res?.handedness || [];

      // Build the new payload shape.
      const hands = handsRaw.map((landmarks, i) => ({
        landmarks,
        handedness: handedness[i]?.[0]?.categoryName || 'Right',
        score: handedness[i]?.[0]?.score ?? 0,
      }));

      // Draw — first hand violet, second hand cyan
      hands.forEach((h, i) => {
        if (i === 0) {
          drawHand(ctx, h.landmarks, c.width, c.height);
        } else {
          drawHand(ctx, h.landmarks, c.width, c.height, SECOND_HAND_COLOR);
        }
      });

      // hands:count event (only on change — keeps UI updates throttled)
      if (hands.length !== this._lastHandCount) {
        this._lastHandCount = hands.length;
        bus.emit(EVENTS.HANDS_COUNT, hands.length);
      }

      // hand:status (idle | detecting) — only on transition
      const newHandStatus = hands.length > 0 ? 'detecting' : 'idle';
      if (newHandStatus !== this._lastHandStatus) {
        this._lastHandStatus = newHandStatus;
        bus.emit(EVENTS.HAND_STATUS, { status: newHandStatus });
      }

      bus.emit(EVENTS.LANDMARKS_DETECTED, { hands, timestamp: now });

      // FPS rolling 3s
      this.fpsBuf.push(now);
      const cutoff = now - 3000;
      while (this.fpsBuf.length && this.fpsBuf[0] < cutoff) this.fpsBuf.shift();
      if (this.fpsBuf.length >= 2) {
        const dt = (this.fpsBuf[this.fpsBuf.length - 1] - this.fpsBuf[0]) / 1000;
        const fps = dt > 0 ? Math.round((this.fpsBuf.length - 1) / dt) : 0;
        if (fps !== this.fps) {
          this.fps = fps;
          bus.emit(EVENTS.FPS, fps);
        }
      }

      // Auto-degrade sampler (one-shot — no flapping)
      if (!this._samplerDone && this.numHands === 2) {
        if (this._samplerStartedAt === null) {
          this._samplerStartedAt = now;
        } else if (now - this._samplerStartedAt >= FPS_SAMPLER_DURATION_MS) {
          this._samplerDone = true;
          if (this.fps > 0 && this.fps < FPS_DEGRADE_THRESHOLD) {
            this._degradeToSingleHand().catch((e) => {
              // eslint-disable-next-line no-console
              console.warn('[SignVoice] degrade failed:', e);
            });
          } else {
            // eslint-disable-next-line no-console
            console.log(`[SignVoice] Dual-hand confirmed. FPS=${this.fps}`);
          }
        }
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.debug('[VisionAgent] detect skip:', String(e?.message || e));
    }
  }

  destroy() {
    this.pause();
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    if (this.landmarker) {
      try {
        this.landmarker.close();
      } catch {
        /* noop */
      }
      this.landmarker = null;
    }
    this.setState('idle');
  }
}
